import { useMemo } from 'react'
import { collection, limit, orderBy, query, Timestamp, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { useSeenWatermark } from './useSeenWatermark'
import { useAuth } from './useAuth'
import { useNow } from './useNow'
import type { RoutineDispatchState, RoutineName, RoutineRun } from '../types'

const EMPTY: RoutineRun[] = []

// Four lanes on a daily-ish cron produce a few hundred runs a year, and the
// panel only ever answers "what happened lately" — a window is enough, and it
// keeps the payload bounded as the collection grows without a cleanup job.
const WINDOW = 200

const FAILED_WINDOW_DAYS = 7
const DAY_MS = 24 * 60 * 60 * 1000

// Every scheduled routine run, newest first. Rules make the collection
// admin-read-only, so non-admins run no query at all rather than hitting a
// permission error from the nav chrome.
export function useAdminRoutineRuns() {
  const { isAdmin } = useAuth()

  const runsQuery = useMemo(
    () =>
      isAdmin
        ? (query(
            collection(db, 'routineRuns'),
            orderBy('finishedAt', 'desc'),
            limit(WINDOW),
          ) as Query<RoutineRun>)
        : null,
    [isAdmin],
  )

  const { data, loading, error } = useFirestoreCollection<RoutineRun>(runsQuery)

  return { runs: isAdmin ? data : EMPTY, loading, error }
}

// Every run that finished in the last `days` days, for the metrics dashboard. A
// trend needs the whole window regardless of how many noop runs it holds, so this
// is time-bounded rather than the runs list's fixed 200. The cutoff is
// day-granular so the listener only changes once a day.
export function useRoutineRunsSince(days: number) {
  const { isAdmin } = useAuth()
  const now = useNow(60 * 60 * 1000)
  const cutoff = Math.floor(now.getTime() / DAY_MS) * DAY_MS - (days - 1) * DAY_MS

  const runsQuery = useMemo(
    () =>
      isAdmin
        ? (query(
            collection(db, 'routineRuns'),
            where('finishedAt', '>=', Timestamp.fromMillis(cutoff)),
            orderBy('finishedAt', 'asc'),
          ) as Query<RoutineRun>)
        : null,
    [isAdmin, cutoff],
  )
  const { data, loading, error } = useFirestoreCollection<RoutineRun>(runsQuery)
  return { runs: isAdmin ? data : EMPTY, loading, error, since: cutoff, now: now.getTime() }
}

// One routine's latest runs, newest first.
export function useLatestRoutineRuns(routine: RoutineName, count: number) {
  const { isAdmin } = useAuth()
  const runsQuery = useMemo(
    () =>
      isAdmin
        ? (query(
            collection(db, 'routineRuns'),
            where('routine', '==', routine),
            orderBy('finishedAt', 'desc'),
            limit(count),
          ) as Query<RoutineRun>)
        : null,
    [isAdmin, routine, count],
  )
  const { data, loading, error } = useFirestoreCollection<RoutineRun>(runsQuery)
  return { runs: isAdmin ? data : EMPTY, loading, error }
}

export interface RoutineFireError {
  routine: RoutineName
  error: string
  since: number
}

/**
 * Routines whose last on-demand fire failed (revoked token, rate limit, beta
 * header change). The backstop cron hides a broken trigger, so this stays up
 * until a fire succeeds — it is a standing condition, not an event to mark seen.
 */
export function useRoutineFireErrors(): RoutineFireError[] {
  const { isAdmin } = useAuth()
  const dispatchQuery = useMemo(
    () => (isAdmin ? (collection(db, 'routineDispatch') as Query<RoutineDispatchState & { id: string }>) : null),
    [isAdmin],
  )
  const { data } = useFirestoreCollection<RoutineDispatchState & { id: string }>(dispatchQuery)
  return useMemo(
    () =>
      data
        .filter(d => d.lastError)
        .map(d => ({ routine: d.id as RoutineName, error: d.lastError!, since: d.lastErrorAt ?? 0 })),
    [data],
  )
}

/**
 * Failed runs the admin hasn't looked at yet, for the admin panel row and the
 * nav chrome total. A run isn't a queue item and nothing triages it, so the
 * only thing that clears it is the `users/{uid}/private/routineRunsSeen`
 * watermark — same model as `useRequestsUnread`. Pass `active` from the
 * routine runs page so opening it advances the watermark; every other caller
 * passes false and reads the count. The rolling week still bounds it, so a
 * lane that failed for a month while nobody was looking doesn't badge a month
 * of history. Routines whose on-demand fire is failing count too, for as long
 * as it keeps failing.
 */
export function useUnreadFailedRoutineRunCount(active: boolean): number {
  const { isAdmin, signedInUid } = useAuth()
  const { failures, loading: runsLoading } = useRecentFailedRunTimes()
  const fireErrors = useRoutineFireErrors().length

  const { unread, settled } = useSeenWatermark({
    uid: isAdmin ? signedInUid : null,
    docName: 'routineRunsSeen',
    times: failures,
    active,
    itemsLoading: runsLoading,
    logTag: 'routines',
  })
  return settled ? unread + fireErrors : 0
}


// finishedAt of the failed runs in the rolling week. Mounted on every page for
// admins (the nav badge), so it asks for failures only rather than reading the
// 200-run window useAdminRoutineRuns keeps for the runs page. The cutoff is
// day-granular so the query — a new listener, billed in full — changes once a
// day, not on every tick.
function useRecentFailedRunTimes(): { failures: number[]; loading: boolean } {
  const { isAdmin } = useAuth()
  const now = useNow(60 * 60 * 1000)
  const cutoff = Math.floor(now.getTime() / DAY_MS) * DAY_MS - FAILED_WINDOW_DAYS * DAY_MS

  const failedQuery = useMemo(
    () =>
      isAdmin
        ? (query(
            collection(db, 'routineRuns'),
            where('status', '==', 'failed'),
            where('finishedAt', '>=', Timestamp.fromMillis(cutoff)),
          ) as Query<RoutineRun>)
        : null,
    [isAdmin, cutoff],
  )
  const { data, loading } = useFirestoreCollection<RoutineRun>(failedQuery)
  const failures = useMemo(() => data.map(r => r.finishedAt?.toMillis() ?? 0), [data])
  return { failures, loading }
}
